import {isFeatureEnabled} from 'feature-flags';
import {Button, Card, Chips, closeCurrentDialog, openDialog} from 'platform/components';
import {Center, Hide, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNilOrEmpty, isNotNilOrEmpty, noop} from 'ramda-adjunct';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  useCreateBusinessCaseMutation,
  useCreateSaleBusinessCaseMutation,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds, vehiclesRoutes} from '@dms/routes';
import {
  CustomerWidgetCard,
  handleApiError,
  queryParams,
  SnippetProps,
  useCodeList,
  useLazyGetAuthorizationProfilesAndCKKPermissions,
  usePermissions,
} from '@dms/shared';

import {buildObject, composePath, useNavigate, useQueryState} from 'shared/web';

import {handleSaleVehicleSelectionError} from '../../components/BusinessCaseOffers/BusinessCaseSaleVehicles/OfferAlternatives/handleOfferAlternativeError';
import {SelectVehicleDialogDataGrid} from '../../components/BusinessCaseOffers/SelectVehicleDialogDataGrid';
import {BusinessCaseConceptPage} from './components/BusinessCaseConceptPage';
import {CreateSourcingPurchaseBusinessCase} from './CreateSourcingPurchaseBusinessCase';

// composePath keeps the current query, so the branch confirmed on the vehicle card is dropped on
// the way out; otherwise it would reach the next create flow for a vehicle nobody confirmed it for
const WITHOUT_CONFIRMED_BRANCH = {[queryParams.BRANCH_ID]: undefined};

export function CreateBusinessCase() {
  const [sourcingExternalId] = useQueryState(queryParams.SOURCING_EXTERNAL_ID);

  return sourcingExternalId ? (
    <CreateSourcingPurchaseBusinessCase key={sourcingExternalId} externalId={sourcingExternalId} />
  ) : (
    <CreateBusinessCaseRegular />
  );
}

function CreateBusinessCaseRegular() {
  const [vehicleIdQuery] = useQueryState('vehicleId');
  const [branchIdQuery] = useQueryState(queryParams.BRANCH_ID);
  const navigate = useNavigate();

  const [vehicleId, setVehicleId] = useState(vehicleIdQuery ?? '');
  // The branch confirmed on the vehicle card applies only while that vehicle is still selected
  const xBranch =
    isNotNilOrEmpty(vehicleId) && vehicleId === vehicleIdQuery
      ? (branchIdQuery ?? undefined)
      : undefined;
  const [sourceCodeId, setSourceCodeId] = useState<string | null>(null);

  const {data: vehicleDetail} = useGetVehicleQuery({vehicleId}, {skip: isNilOrEmpty(vehicleId)});
  const [businessCaseSourcesCodelist] = useCodeList('interest_source');

  const [createBusinessCaseMutation, {isLoading: isCreateBusinessCaseLoading}] =
    useCreateBusinessCaseMutation();
  const [createSaleBusinessCaseMutation, {isLoading: isCreateSaleBusinessCaseLoading}] =
    useCreateSaleBusinessCaseMutation();
  const {handleSearchAuthorizationProfiles} = useLazyGetAuthorizationProfilesAndCKKPermissions();
  const [getBusinessCase] = useLazyGetBusinessCaseQuery();

  const [canCreateBusinessCaseSelling] = usePermissions({
    permissionKeys: ['createBusinessCaseSelling'],
  });

  const onCustomerSelected = (customer: CustomerResponseBodyV2) => {
    if (isCreateBusinessCaseLoading || isCreateSaleBusinessCaseLoading) {
      return;
    }
    if (isNotNilOrEmpty(vehicleDetail)) {
      createSaleBusinessCaseMutation({
        createSaleBusinessCaseRequestBody: {
          customerId: customer.id,
          vehicleToSaleId: vehicleId,
          sourceCodeId,
        },
        xBranch,
      })
        .unwrap()
        .then((businessCase) => {
          navigate(
            composePath(businessCaseRoutes.selling, {
              params: {id: businessCase.id},
              queryParams: WITHOUT_CONFIRMED_BRANCH,
            }),
            {
              state: {
                ignoreUnmountEvents: true,
              },
            }
          );
          getBusinessCase({businessCaseId: businessCase?.id})
            .unwrap()
            .then((businessCase) => {
              handleSearchAuthorizationProfiles(
                {
                  vehicleMake: vehicleDetail?.make ?? '',
                  vehicleType: vehicleDetail?.type ?? null,
                  vehicleModelFamily: vehicleDetail?.vehicle?.modelFamily ?? null,
                  vehicleModelFamilyGroup: vehicleDetail?.vehicle?.modelFamilyGroup ?? null,
                },
                customer.id,
                {bcNumber: businessCase?.code, bcCreatedAt: businessCase?.createdAt}
              );
            })
            .catch(handleApiError);
        })
        .catch(handleSaleVehicleSelectionError);
    } else {
      createBusinessCaseMutation({
        createBusinessCaseRequestBody: {
          customerId: customer?.id,
          vehicleToSaleId: vehicleId || undefined,
          sourceCodeId,
        },
        xBranch,
      })
        .unwrap()
        .then((businessCase) => {
          navigate(
            composePath(
              businessCaseRoutes[
                businessCase.businessCaseType === 'SELLING' ? 'selling' : 'buying'
              ],
              {params: {id: businessCase.id}, queryParams: WITHOUT_CONFIRMED_BRANCH}
            )
          );
        })
        .catch(handleSaleVehicleSelectionError);
    }
  };

  const handleCreateSaleVehicle = () => {
    closeCurrentDialog();
    navigate(
      composePath(vehiclesRoutes.createV2, {
        queryParams: {
          ...WITHOUT_CONFIRMED_BRANCH,
          [queryParams.REDIRECT_SOURCE]: EntityResourceIds.businessCase,
          [queryParams.RETURN_URL]: businessCaseRoutes.concept,
        },
      })
    );
  };

  const handleSelectVehicle = () => {
    openDialog(
      <SelectVehicleDialogDataGrid
        data-testid={testIds.businessCase.detail('addSaleVehicle')}
        onRowSelectionChange={(rows) => {
          setVehicleId(rows[0]?.id);
        }}
      />,
      {
        title: i18n.t('page.businessCase.labels.selectVehicle'),
        size: 'large',
        // It is a product requirement to have a button here (in the dialog header, next to
        // the close button), but we do not want to extend or update the platform Dialog
        // component because of one case. We also will not rename the existing property nor
        // add a new one because of this one case. So we use the `pagination` slot
        // as an escape hatch to satisfy the product requirement.
        pagination: (
          <Button
            title={i18n.t('entity.vehicle.labels.createVehicle')}
            onClick={handleCreateSaleVehicle}
            isDisabled={!canCreateBusinessCaseSelling}
            data-testid={testIds.businessCase.concept('createSaleVehicle')}
          />
        ),
        buttons: [
          {
            title: i18n.t('general.actions.cancel'),
            variant: 'secondary',
            onClick: closeCurrentDialog,
          },
          {
            title: i18n.t('general.labels.select'),
            variant: 'primary',
            onClick: closeCurrentDialog,
          },
        ],
      }
    );
  };

  const customerContent = isFeatureEnabled(featureFlags.BC_SOURCE) ? (
    <Center>
      <VStack spacing={4} maxWidth={170} width="100%">
        <Card title={i18n.t('entity.interest.labels.source')}>
          <Chips
            value={sourceCodeId ? [sourceCodeId] : []}
            onChange={(values) => setSourceCodeId(values?.[0] ?? null)}
            options={
              businessCaseSourcesCodelist?.map((item) => ({
                label: item.name,
                value: item.codeId,
              })) ?? []
            }
            isMultiple={false}
            data-testid={testIds.businessCase.concept('source')}
          />
        </Card>
        <Hide when={isNotNilOrEmpty(sourceCodeId)}>
          <Card title={i18n.t('entity.customer.labels.customer')}>
            <Text color="tertiary">{i18n.t('page.businessCase.labels.selectSourceFirst')}</Text>
          </Card>
        </Hide>
        <Show when={isNotNilOrEmpty(sourceCodeId)}>
          <CustomerWidgetCard
            customTitle={i18n.t('entity.customer.labels.customer')}
            secondStepComponentType="BUSINESS_CASE"
            onCustomer={onCustomerSelected}
            isLoadingOnCustomer={isCreateBusinessCaseLoading || isCreateSaleBusinessCaseLoading}
            shouldWarnBeforeActiveBusinessCaseEdit
            data-testid={testIds.businessCase.concept('customerWidget')}
            vehicleId={vehicleId}
          />
        </Show>
      </VStack>
    </Center>
  ) : (
    <Center>
      <VStack spacing={4} maxWidth={170} width="100%">
        <CustomerWidgetCard
          customTitle={i18n.t('entity.customer.labels.customer')}
          secondStepComponentType="BUSINESS_CASE"
          onCustomer={onCustomerSelected}
          isLoadingOnCustomer={isCreateBusinessCaseLoading || isCreateSaleBusinessCaseLoading}
          shouldWarnBeforeActiveBusinessCaseEdit
          data-testid={testIds.businessCase.concept('customerWidget')}
          vehicleId={vehicleId}
        />
      </VStack>
    </Center>
  );

  return (
    <BusinessCaseConceptPage
      customerContent={customerContent}
      customerHref={businessCaseRoutes.concept}
      sellingSnippet={buildObject<SnippetProps>()
        .label(i18n.t('page.businessCase.labels.selling'))
        .placeholder({
          title: i18n.t('entity.vehicle.actions.addVehicleToSell'),
          onClick: handleSelectVehicle,
        })
        .title(vehicleDetail?.title)
        .parameters([
          vehicleDetail?.state?.registrationPlate ||
            i18n.t('entity.vehicle.notifications.noLicencePlate'),
          vehicleDetail?.vin || i18n.t('entity.vehicle.notifications.noVIN'),
        ])
        .href(
          vehicleDetail
            ? composePath(vehiclesRoutes.detail, {
                params: {id: vehicleDetail.id},
                queryParams: WITHOUT_CONFIRMED_BRANCH,
              })
            : undefined
        )
        .isDisabled(!canCreateBusinessCaseSelling)
        .build()}
      buyingSnippet={{
        label: i18n.t('entity.businessCase.labels.buying'),
        placeholder: {
          title: i18n.t('entity.vehicle.actions.addVehicleToBuy'),
          onClick: noop,
        },
        isDisabled: true,
      }}
    />
  );
}
